import { Close as CloseIcon, AssignmentOutlined as ContractIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import type { Dayjs } from 'dayjs';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import WebGrid from '../../webgrid/src';
import { useAuth } from '../contexts/AuthContext';
import {
    createDateColumn,
    getContractAmountColumns,
    getContractDateColumns,
} from '../domain/contractColumns';
import { buildPawnedItemRows } from '../domain/contractPawnedItems';
import { getPostponeBlockingStatus } from '../domain/contractPostponement';
import { resolveClientStanding } from '../domain/contracts/clientStanding';
import { summariseClientContracts } from '../domain/contracts/clientContractSummary';
import {
    buildLoyaltyCategories,
    resolveCategoryChoice,
    type CategoryOverride,
} from '../domain/contracts/loyaltyCategory';
import type { LoyaltySettingsPayload } from '../domain/loyaltySettings';
import useQuery, { useDeferredQuery } from '../hooks/useQuery';
import useContractPawnedItems from '../hooks/useContractPawnedItems';
import useRequest from '../hooks/useRequest';
import useCashBookingReadiness from '../hooks/useCashBookingReadiness';
import { CashBookingRefusalDialog } from '../components/CashBookingRefusal';
import useRouteSelectedClient from '../hooks/useRouteSelectedClient';
import { useAuthenticatedImage } from '../hooks/useAuthenticatedImage';
import {
    ContractPrintBlockedError,
    useContractDocument,
    ProgressCallback,
} from '../utils/contractDocument';
import ClientDetailsRow from '../components/contracts/ClientDetailsRow';
import ClientPhoto from '../components/contracts/ClientPhoto';
import ClientSummary from '../components/contracts/ClientSummary';
import ProgressModal from '../components/ProgressModal';
import ContractHistoryModal from '../components/ContractHistoryModal';
import ContractOutputModal, { ContractOutputAction } from '../components/ContractOutputModal';
import ContractPdfPreviewDialog from '../components/ContractPdfPreviewDialog';
import { useSumator } from '../contexts/SumatorContext';
import { parseContractReference } from '../domain/contractReference';
import ContractObjectReturnsPanel from '../components/contracts/ContractObjectReturnsPanel';
import AdvancedContractSearchDialog from '../components/contracts/AdvancedContractSearchDialog';
import ContractStatusChip from '../components/contracts/ContractStatusChip';
import SpecialRateDialog, { SpecialRateTarget } from '../components/contracts/SpecialRateDialog';
import { canSetSpecialRate } from '../domain/contracts/specialRate';
import { mapDatabaseStatusToDisplay } from '../domain/contractStatus';

interface ContractData {
    Nr: number;
    NrContract: string;
    Statut: string;
    Nume_client: string;
    obiect_amanetat: string;
    SumainEuro: number;
    SumainLei: number;
    Curs_BNR?: number;
    Comision_zi?: number;
    Durata?: number;
    Termen_restituire?: string;
    Data_Exp: string;
    Data?: string;
    Clnr?: number;
    Observatii?: string;
    quantity?: string;
    extended_due_date?: string;
    /** Delphi's special-rate flag (BL-05): the contract gets no discount at payment. */
    specialRate?: boolean;
    [key: string]: unknown;
}

interface ClientData {
    ClNr: number;
    Nume: string;
    CNP?: string;
    Oras?: string; // Changed from Localitate to match backend
    Str?: string; // Changed from Strada to match backend
    Nr?: string;
    Ap?: string;
    NrContract?: string;
    photoUrl?: string;
    Observatii?: string;
    Notite?: string;
    Telefon?: string;
    Mobil?: string;
    Email?: string;
    Judet?: string;
    CodPostal?: string;
    SerieCI?: string;
    NrCI?: string;
    EmitentCI?: string;
    DataEliberareCI?: string;
    DataExpirareCI?: string;
    Delegate?: string;
    TaxCode?: string;
    RegistrationNumber?: string;
    /** Client tier (VIP, Gold, Silver, Diamond, Bonus, Bonus+), from `client.category`. */
    Categorie?: string | null;
    IsDisabled?: boolean;
    LinkedToClientId?: number | null;
    [key: string]: unknown;
}

interface ClientEditData {
    Nume: string;
    Oras: string;
    Str: string;
    Nr: string;
    Ap: string;
    Judet: string;
    SerieCI: string;
    NrCI: string;
    EmitentCI: string;
    Telefon: string;
    CNP: string;
    Delegate: string;
    TaxCode: string;
    RegistrationNumber: string;
    IsDisabled: boolean;
}

interface Pagination {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasNext: boolean;
    hasPrev: boolean;
}

interface FormData {
    clientId: number;
    obiectAmanetat: string;
    sumainEuro: number;
    sumainLei: number;
    curs: number;
    durata: number;
    comisionZi: number;
    observatii: string;
    statut: string;
}

interface FormErrors {
    clientId?: string;
    obiectAmanetat?: string;
    sumainEuro?: string;
    sumainLei?: string;
    curs?: string;
    durata?: string;
    observatii?: string;
    statut?: string;
}

/** Same admin test the rest of the app uses (Header, Clients, GoodsSale). */
const isAdminRole = (role: string | undefined): boolean => {
    const normalised = role?.toLowerCase();
    return normalised === 'admin' || normalised === 'administrator';
};

const ContractsInner = () => {
    const { addLine, clearSumator, multiClient } = useSumator();
    const [modalVisible, setModalVisible] = useState(false);
    const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
    const contractsPageRef = useRef<HTMLDivElement>(null);
    const [editingContract, setEditingContract] = useState<ContractData | null>(null);
    const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
    const routeSelectedClientIdRef = useRef<number | null>(null);
    const prevClientFiltersRef = useRef<Record<string, string>>({});

    const handleClientSelection = (newClient: ClientData | null) => {
        const isRouteHydration = routeSelectedClientIdRef.current === newClient?.ClNr;
        routeSelectedClientIdRef.current = null;

        if (!isRouteHydration && !multiClient && newClient?.ClNr !== selectedClient?.ClNr) {
            clearSumator();
        }
        setSelectedClient(newClient);
    };

    const [contractNumberSearch, setContractNumberSearch] = useState<string>('');
    const [contractSearchLoading, setContractSearchLoading] = useState<boolean>(false);
    const [clientGridInitialFilters, setClientGridInitialFilters] = useState<
        Record<string, string>
    >({});
    const [postponeDate, setPostponeDate] = useState<Dayjs | null>(null);
    // The category the user picked by hand, with the client it was picked for, so that it
    // lapses on its own when another client is selected and the proposal takes over again.
    const [categoryOverride, setCategoryOverride] = useState<CategoryOverride | null>(null);
    const [categorySaving, setCategorySaving] = useState(false);
    const [clientInfoModalOpen, setClientInfoModalOpen] = useState(false);
    const [mergeClientModalOpen, setMergeClientModalOpen] = useState(false);
    const [mergeTargetClientId, setMergeTargetClientId] = useState<number | ''>('');
    const [clientSaving, setClientSaving] = useState(false);
    const [clientMerging, setClientMerging] = useState(false);
    const [clientEditData, setClientEditData] = useState<ClientEditData>({
        Nume: '',
        Oras: '',
        Str: '',
        Nr: '',
        Ap: '',
        Judet: '',
        SerieCI: '',
        NrCI: '',
        EmitentCI: '',
        Telefon: '',
        CNP: '',
        Delegate: '',
        TaxCode: '',
        RegistrationNumber: '',
        IsDisabled: false,
    });
    const [notesText, setNotesText] = useState<string>('');
    const [notesSaving, setNotesSaving] = useState<boolean>(false);
    const [formData, setFormData] = useState<FormData>({
        clientId: 0,
        obiectAmanetat: '',
        sumainEuro: 0,
        sumainLei: 0,
        curs: 5.0,
        durata: 30,
        comisionZi: 0.01,
        observatii: '',
        statut: 'ACTIV',
    });
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [selectedContracts, setSelectedContracts] = useState<ContractData[]>([]);
    const [postponeConfirmOpen, setPostponeConfirmOpen] = useState(false);
    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
        contract: ContractData | null;
    } | null>(null);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [specialRateContract, setSpecialRateContract] = useState<SpecialRateTarget | null>(null);
    const [selectedHistoryContractNr, setSelectedHistoryContractNr] = useState<number | null>(null);
    const [alertMessage, setAlertMessage] = useState<{
        type: 'success' | 'error';
        text: string;
    } | null>(null);
    const [bookingRefusalKey, setBookingRefusalKey] = useState<string | null>(null);
    const { ensureCanBook } = useCashBookingReadiness();
    // PDF generation removed
    const request = useRequest();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { downloadPdfDocument, printPdfDocument, checkPdfConversionStatus } =
        useContractDocument(t);

    // PDF conversion state
    const [pdfStatusChecked, setPdfStatusChecked] = useState(false);

    // Contract output (save PDF / print) modal state
    const [outputModalOpen, setOutputModalOpen] = useState(false);

    // A contract the browser would not print directly, shown for its viewer to print instead
    const [blockedPrintDocument, setBlockedPrintDocument] = useState<{
        number: string;
        url: string;
    } | null>(null);

    // Progress modal state
    const [progressModalOpen, setProgressModalOpen] = useState(false);
    const [progressTitle, setProgressTitle] = useState('');
    const [progressValue, setProgressValue] = useState(0);
    const [progressStatus, setProgressStatus] = useState<
        'preparing' | 'processing' | 'completed' | 'error'
    >('preparing');
    const [progressMessage, setProgressMessage] = useState<string>('');

    // Fetched with the session's token and held as an in-memory object URL: a portrait is
    // personal data and is no longer reachable by pointing <img src> at a public path.
    const { objectUrl: selectedClientPhotoUrl } = useAuthenticatedImage(selectedClient?.photoUrl);

    // Only load contracts when a client is selected
    const {
        data: contractsData,
        loading: contractsLoading,
        error: contractsError,
        reload: reloadContracts,
    } = useDeferredQuery<{ contracts: ContractData[]; pagination: Pagination }>({
        path: selectedClient
            ? `/contracts?clnr=${selectedClient.ClNr}&limit=10000` // Load all contracts for the client
            : '/contracts?limit=0', // Load empty when no client selected
        method: 'GET',
        shouldExecute: !!selectedClient,
    });

    const {
        data: clientsData,
        loading: clientsLoading,
        reload: reloadClients,
    } = useQuery<{
        clients: ClientData[];
        pagination: Pagination;
    }>({
        path: '/clients?limit=100000',
        method: 'GET',
    });

    const { data: loyaltyData } = useQuery<{ data?: LoyaltySettingsPayload }>({
        path: '/configuration/loyalty',
        method: 'GET',
    });

    // Memoised so the standing below (and anything else keyed on it) is not recomputed
    // on every render by a fresh `[]` fallback.
    const contracts = useMemo(() => contractsData?.contracts ?? [], [contractsData?.contracts]);
    const clients = useMemo(() => clientsData?.clients ?? [], [clientsData?.clients]);
    const activeMergeCandidates = useMemo(
        () => clients.filter(client => client.ClNr !== selectedClient?.ClNr && !client.IsDisabled),
        [clients, selectedClient?.ClNr],
    );

    // Show contracts only when a client is selected
    const filteredContracts = selectedClient ? contracts : [];

    // The Gyurci theme paints the page background from the selected client's standing:
    // green while they are clear, red once a long contract of theirs has expired. Themes
    // that do not style these classes are unaffected.
    const clientStanding = useMemo(
        () =>
            resolveClientStanding(contracts, {
                selectedClientId: selectedClient?.ClNr ?? null,
                // The hook reports `{loading: false, data: undefined}` while it is idle and
                // `loading: undefined` for a failed load, so the data itself has to be there:
                // "not loading" on its own would paint an empty list as a clean client.
                contractsLoaded: contractsLoading === false && contractsData !== undefined,
            }),
        [contracts, contractsData, selectedClient, contractsLoading],
    );

    // Administrators get the client's history in figures under the contracts grid. Computed
    // from the contracts already loaded for the client, so it needs no extra request and can
    // never disagree with the grid above it.
    const isAdmin = isAdminRole(user?.role);
    const clientSummary = useMemo(() => summariseClientContracts(contracts), [contracts]);

    // The category dropdown offers the categories of the `bonus` table, as the Configure page's
    // Loyalty tab edits them. It starts on the one the client's spending earns (their net
    // profit), once their contracts are in, and the user may pick another.
    const loyaltyCategories = useMemo(
        () => buildLoyaltyCategories(loyaltyData?.data?.categories),
        [loyaltyData?.data?.categories],
    );
    const {
        selected: selectedCategory,
        options: categoryOptions,
        unchanged: categoryUnchanged,
    } = resolveCategoryChoice({
        categories: loyaltyCategories,
        clientNr: selectedClient?.ClNr,
        currentCategory: selectedClient?.Categorie,
        netProfit: clientSummary.netProfit,
        contractsLoaded: contractsLoading === false && contractsData !== undefined,
        override: categoryOverride,
    });

    // Postponement always targets the contract selected in the contracts grid
    const postponeContract = selectedContracts[0] ?? null;
    const { pawnedItems, pawnedItemsLoading } = useContractPawnedItems(postponeContract?.Nr);
    const pawnedItemRows = buildPawnedItemRows(pawnedItems, postponeContract?.obiect_amanetat);
    // Redeemed (OK), stock (INV), sold (EXP) and cancelled (ANL) contracts cannot be postponed
    const postponeBlockingStatus = getPostponeBlockingStatus(postponeContract);
    const postponeDisabled = !postponeContract || postponeBlockingStatus !== null;

    // Always show all clients (newest first) so the user can browse and select
    const filteredClients = clients;

    // Client grid columns for the top section
    const clientColumns = [
        {
            key: 'ClNr',
            title: 'ID',
            dataIndex: 'ClNr',
            sortable: true,
            filterable: true,
            width: 60,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'Nume',
            title: t('clients.name'),
            dataIndex: 'Nume',
            sortable: true,
            filterable: true,
            width: 120,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'CNP',
            title: 'CNP',
            dataIndex: 'CNP',
            sortable: false,
            filterable: true,
            width: 100,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'Oras',
            title: t('clients.city'),
            dataIndex: 'Oras',
            sortable: false,
            filterable: true,
            width: 90,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'Str',
            title: t('clients.street'),
            dataIndex: 'Str',
            sortable: false,
            filterable: true,
            width: 120,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'Nr',
            title: 'Nr',
            dataIndex: 'Nr',
            sortable: false,
            filterable: true,
            width: 40,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'Telefon',
            title: 'Telefon',
            dataIndex: 'Telefon',
            sortable: false,
            filterable: true,
            width: 100,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'NrContract',
            title: t('contracts.number'),
            dataIndex: 'NrContract',
            sortable: false,
            filterable: true,
            width: 60,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
    ];

    // Handle contract loading errors
    useEffect(() => {
        if (contractsError) {
            setAlertMessage({ type: 'error', text: 'Failed to load contracts' });
            console.error('Load contracts error:', contractsError);
        }
    }, [contractsError]);

    // Clear alert messages after 5 seconds
    useEffect(() => {
        if (alertMessage) {
            const timer = setTimeout(() => setAlertMessage(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [alertMessage]);

    // Handle client selection changes - reload contracts when client changes
    useEffect(() => {
        if (selectedClient) {
            reloadContracts();
        }
    }, [selectedClient, reloadContracts]);

    // Sync notes editor with the currently selected client
    useEffect(() => {
        setNotesText(String(selectedClient?.Notite ?? selectedClient?.Observatii ?? ''));
    }, [selectedClient]);

    // A contract selection only makes sense for the client it belongs to
    useEffect(() => {
        setSelectedContracts([]);
    }, [selectedClient?.ClNr]);

    // Select the client another page navigated here with, once the list has arrived.
    useRouteSelectedClient(clients, !!selectedClient, client => {
        routeSelectedClientIdRef.current = client.ClNr;
        setSelectedClient(client);
        setClientGridInitialFilters({ ClNr: String(client.ClNr), Nume: client.Nume });
    });

    // PDF conversion availability check
    useEffect(() => {
        const checkPdfStatus = async () => {
            try {
                await checkPdfConversionStatus();
            } catch (error) {
                console.warn('Failed to check PDF conversion status:', error);
            } finally {
                setPdfStatusChecked(true);
            }
        };

        if (!pdfStatusChecked) {
            checkPdfStatus();
        }
    }, [checkPdfConversionStatus, pdfStatusChecked]);

    // Function to search for client by contract number
    const handleContractNumberSearch = async (contractNumber: string) => {
        // This box is the web counterpart of the legacy contract search, which stripped the
        // same `--` prefix (`old/delphi/Ucontracte.pas:468`) because a scanner types into
        // whatever field has focus. It accepts all three shapes an operator can produce: a
        // legacy label, a label this application printed, or a typed number.
        //
        // This flow needs only the contract — it resolves the client — so the object letter
        // a canonical label also carries is not used here. The request sends the validated
        // number, which is what the endpoint's strict integer contract expects.
        const scanned = parseContractReference(contractNumber, { allowBareNumber: true });

        if (!scanned.ok) {
            if (scanned.reason !== 'empty') {
                setAlertMessage({
                    type: 'error',
                    text: t('contracts.invalidContractReference'),
                });
            }
            return;
        }

        setContractSearchLoading(true);
        try {
            const response = await request<{ client: ClientData }>({
                path: `/contracts/find-client/${scanned.value.contractNumber}`,
                method: 'GET',
            });

            if (response.client) {
                // Select the found client
                setSelectedClient(response.client);
                setContractNumberSearch('');
            }
        } catch (error) {
            console.error('Contract number search error:', error);
            setAlertMessage({
                type: 'error',
                text: `Contract ${contractNumber} not found or no client associated`,
            });
        } finally {
            setContractSearchLoading(false);
        }
    };

    /** `operation` may resolve to its own success text, which then replaces `successText`. */
    const runContractOutput = async (
        title: string,
        operation: (onProgress: ProgressCallback) => Promise<string | void>,
        successText: string,
        errorPrefix: string,
    ) => {
        setProgressTitle(title);
        setProgressModalOpen(true);
        setProgressValue(0);
        setProgressStatus('preparing');
        setProgressMessage('');

        try {
            const outcome = await operation((progress, status, message) => {
                setProgressValue(progress);
                setProgressStatus(status);
                if (message) setProgressMessage(message);
            });

            setAlertMessage({
                type: 'success',
                text: outcome ?? successText,
            });

            // Keep modal open briefly to show success, then close
            setTimeout(() => {
                setProgressModalOpen(false);
            }, 1500);
        } catch (error) {
            console.error(`${errorPrefix}:`, error);
            setAlertMessage({
                type: 'error',
                text: `${errorPrefix}: ${error instanceof Error ? error.message : String(error)}`,
            });

            // Close modal on error
            setTimeout(() => {
                setProgressModalOpen(false);
            }, 2000);
        }
    };

    const handleSavePdfContract = (contract: ContractData) =>
        runContractOutput(
            t('progress.pdfGeneration'),
            onProgress => downloadPdfDocument(contract.Nr, contract.NrContract, onProgress),
            t('contracts.outputModal.savePdfSuccess'),
            t('contracts.outputModal.savePdfError'),
        );

    const handlePrintContract = (contract: ContractData) =>
        runContractOutput(
            t('progress.printGeneration'),
            async onProgress => {
                try {
                    await printPdfDocument(contract.Nr, onProgress);
                } catch (error) {
                    if (!(error instanceof ContractPrintBlockedError)) {
                        throw error;
                    }
                    setBlockedPrintDocument({
                        number: contract.NrContract,
                        url: window.URL.createObjectURL(error.blob),
                    });
                    return t('contracts.outputModal.printOpenedInPreview');
                }
            },
            t('contracts.outputModal.printSuccess'),
            t('contracts.outputModal.printError'),
        );

    const closeBlockedPrintDocument = () => {
        if (blockedPrintDocument) {
            window.URL.revokeObjectURL(blockedPrintDocument.url);
        }
        setBlockedPrintDocument(null);
    };

    const handleOutputSelect = (action: ContractOutputAction) => {
        setOutputModalOpen(false);
        if (selectedContracts.length === 0) {
            return;
        }
        const contract = selectedContracts[0];
        if (action === 'pdf') {
            handleSavePdfContract(contract);
        } else {
            handlePrintContract(contract);
        }
    };

    // PDF handlers removed

    const saveClientCategory = async () => {
        if (!selectedClient || selectedCategory === '') {
            setAlertMessage({ type: 'error', text: t('contracts.selectClientForData') });
            return;
        }

        setCategorySaving(true);
        try {
            await request({
                path: `/clients/${selectedClient.ClNr}/category`,
                method: 'PATCH',
                params: { category: selectedCategory },
            });
            setSelectedClient(prev => (prev ? { ...prev, Categorie: selectedCategory } : prev));
            setCategoryOverride(null);
            // The client grid is fed by a one-shot query; refresh it so reselecting the client
            // does not bring the old category back.
            reloadClients();
            setAlertMessage({ type: 'success', text: t('contracts.categorySaved') });
        } catch (error) {
            console.error('Save client category error:', error);
            setAlertMessage({ type: 'error', text: t('contracts.categorySaveFailed') });
        } finally {
            setCategorySaving(false);
        }
    };

    const saveClientNotes = async (value: string) => {
        if (!selectedClient) {
            setAlertMessage({ type: 'error', text: t('contracts.selectClientForData') });
            return;
        }

        setNotesSaving(true);
        try {
            await request({
                path: `/clients/${selectedClient.ClNr}/notes`,
                method: 'PATCH',
                params: { notes: value },
            });
            const updatedNotes = value.trim() ? value : '';
            setSelectedClient(prev =>
                prev ? { ...prev, Notite: updatedNotes, Observatii: updatedNotes } : prev,
            );
            setNotesText(updatedNotes);
            // The client grid is fed by a one-shot query, so its rows still carry the old
            // notes. Selecting another client and coming back re-seeds the editor from that
            // stale row and the saved observations look lost - refresh the list as well.
            reloadClients();
            setAlertMessage({ type: 'success', text: t('contracts.notesSaved') });
        } catch (error) {
            console.error('Save client notes error:', error);
            setAlertMessage({ type: 'error', text: t('contracts.notesSaveFailed') });
        } finally {
            setNotesSaving(false);
        }
    };

    const openClientFile = () => {
        if (!selectedClient) {
            return;
        }

        setClientEditData({
            Nume: selectedClient.Nume ?? '',
            Oras: selectedClient.Oras ?? '',
            Str: selectedClient.Str ?? '',
            Nr: selectedClient.Nr ?? '',
            Ap: selectedClient.Ap ?? '',
            Judet: selectedClient.Judet ?? '',
            SerieCI: selectedClient.SerieCI ?? '',
            NrCI: selectedClient.NrCI ?? '',
            EmitentCI: selectedClient.EmitentCI ?? '',
            Telefon: selectedClient.Telefon ?? '',
            CNP: selectedClient.CNP ?? '',
            Delegate: selectedClient.Delegate ?? '',
            TaxCode: selectedClient.TaxCode ?? '',
            RegistrationNumber: selectedClient.RegistrationNumber ?? '',
            IsDisabled: selectedClient.IsDisabled ?? false,
        });
        setMergeTargetClientId('');
        setClientInfoModalOpen(true);
    };

    const updateClientEditField = <Field extends keyof ClientEditData>(
        field: Field,
        value: ClientEditData[Field],
    ) => {
        setClientEditData(current => ({ ...current, [field]: value }));
    };

    const saveClientDetails = async (closeAfterSave = true): Promise<boolean> => {
        if (!selectedClient || !clientEditData.Nume.trim()) {
            setAlertMessage({ type: 'error', text: t('clients.nameRequired') });
            return false;
        }

        setClientSaving(true);
        try {
            await request({
                path: `/clients/${selectedClient.ClNr}`,
                method: 'PUT',
                params: {
                    client_name: clientEditData.Nume,
                    phone: clientEditData.Telefon || null,
                    mobile_number: selectedClient.Mobil ?? null,
                    email: selectedClient.Email ?? null,
                    personal_code: clientEditData.CNP || null,
                    city: clientEditData.Oras || null,
                    street: clientEditData.Str || null,
                    street_number: clientEditData.Nr || null,
                    apartment: clientEditData.Ap || null,
                    county: clientEditData.Judet || null,
                    postal_code: selectedClient.CodPostal ?? null,
                    id_card_series: clientEditData.SerieCI || null,
                    id_card_number: clientEditData.NrCI || null,
                    id_issued_by: clientEditData.EmitentCI || null,
                    id_card_issue_date: selectedClient.DataEliberareCI ?? null,
                    id_card_expiry_date: selectedClient.DataExpirareCI ?? null,
                    notes: selectedClient.Notite ?? null,
                    tax_code: clientEditData.TaxCode || null,
                    registration_number: clientEditData.RegistrationNumber || null,
                    delegate: clientEditData.Delegate || null,
                    is_disabled: clientEditData.IsDisabled,
                },
            });

            const updatedClient: ClientData = {
                ...selectedClient,
                ...clientEditData,
            };
            setSelectedClient(updatedClient);
            setAlertMessage({ type: 'success', text: t('contracts.clientDetailsSaved') });
            reloadClients();
            if (closeAfterSave) {
                setClientInfoModalOpen(false);
            }
            return true;
        } catch (error) {
            console.error('Save client details error:', error);
            setAlertMessage({ type: 'error', text: t('contracts.clientDetailsSaveFailed') });
            return false;
        } finally {
            setClientSaving(false);
        }
    };

    const openMergeClientModal = async () => {
        const saved = await saveClientDetails(false);
        if (saved) {
            setMergeClientModalOpen(true);
        }
    };

    const mergeClient = async () => {
        if (!selectedClient || mergeTargetClientId === '') {
            return;
        }

        setClientMerging(true);
        try {
            await request({
                path: `/clients/${selectedClient.ClNr}/merge`,
                method: 'PATCH',
                params: { targetClientId: mergeTargetClientId, lang: i18n.language },
            });
            setMergeClientModalOpen(false);
            setClientInfoModalOpen(false);
            setMergeTargetClientId('');
            handleClientSelection(null);
            reloadClients();
            setAlertMessage({ type: 'success', text: t('contracts.clientsMerged') });
        } catch (error) {
            console.error('Merge clients error:', error);
            setAlertMessage({ type: 'error', text: t('contracts.clientsMergeFailed') });
        } finally {
            setClientMerging(false);
        }
    };

    const handlePostponeContract = () => {
        if (!postponeContract) {
            setAlertMessage({
                type: 'error',
                text: t('contracts.selectContractForPostpone'),
            });
            return;
        }
        if (postponeBlockingStatus) {
            setAlertMessage({
                type: 'error',
                text: t('contracts.postponeNotAllowedForStatus', {
                    status: postponeBlockingStatus,
                }),
            });
            return;
        }
        if (!postponeDate) {
            setAlertMessage({ type: 'error', text: t('contracts.selectPostponeDate') });
            return;
        }
        setPostponeConfirmOpen(true);
    };

    const executePostpone = async (mode: 'single' | 'all') => {
        setPostponeConfirmOpen(false);
        if (!postponeDate || !postponeContract) return;

        const formattedDate = postponeDate.format('YYYY-MM-DD');

        try {
            if (mode === 'single') {
                const result = await request<{
                    success: boolean;
                    requiresApproval: boolean;
                    message: string;
                }>({
                    path: `/contracts/${postponeContract.Nr}/postpone`,
                    method: 'POST',
                    params: { postponeDate: formattedDate },
                });
                if (result.success) {
                    setAlertMessage({ type: 'success', text: result.message });
                    setPostponeDate(null);
                    reloadContracts();
                }
            } else {
                // ALL: postpone every expired contract of the client
                const clientNr = postponeContract.Clnr;
                const expiredContracts = contracts.filter(
                    c => c.Clnr === clientNr && c.Statut === '!!',
                );
                if (expiredContracts.length === 0) {
                    setAlertMessage({
                        type: 'error',
                        text: 'No expired contracts found for this client',
                    });
                    return;
                }
                await Promise.all(
                    expiredContracts.map(c =>
                        request<{ success: boolean; requiresApproval: boolean; message: string }>({
                            path: `/contracts/${c.Nr}/postpone`,
                            method: 'POST',
                            params: { postponeDate: formattedDate },
                        }),
                    ),
                );
                setAlertMessage({
                    type: 'success',
                    text: `${expiredContracts.length} contract(s) postponed`,
                });
                setPostponeDate(null);
                reloadContracts();
            }
        } catch (error) {
            console.error('Postpone error:', error);
            setAlertMessage({
                type: 'error',
                text: error instanceof Error ? error.message : 'Failed to postpone contract',
            });
        }
    };

    /**
     * Opens a page that books money - a new contract's payout, a payment - only when this
     * workstation can book it now; otherwise says why at once, rather than after the operator
     * has filled the page in and the save is refused.
     */
    const openBookingPage = async (path: string) => {
        const refusalKey = await ensureCanBook();
        if (refusalKey) {
            setBookingRefusalKey(refusalKey);
            return;
        }
        navigate(path);
    };

    const handleAddContract = () => {
        // Navigate to add contract page with optional client ID
        void openBookingPage(
            selectedClient ? `/contracts/add?clientId=${selectedClient.ClNr}` : '/contracts/add',
        );
    };

    // Helper function to map display status back to database values
    const mapDisplayStatusToDatabase = (displayStatus: string) => {
        switch (displayStatus) {
            case 'ACTIV':
                return null; // Active contracts have null or empty status
            case 'PASIV':
                return '!';
            case 'EXPIRAT':
                return '!!';
            case 'INV':
                return 'INV'; // Extended contracts are also marked as expired
            case 'EXP':
                return 'EXP';
            case 'OK':
                return 'OK';
            default:
                return displayStatus;
        }
    };

    const handleEditContract = (contract: ContractData) => {
        setEditingContract(contract);
        setFormData({
            clientId: contract.Clnr ?? 0,
            obiectAmanetat: contract.obiect_amanetat ?? '',
            sumainEuro: contract.SumainEuro ?? 0,
            sumainLei: contract.SumainLei ?? 0,
            curs: contract.Curs_BNR ?? 5.0,
            durata: contract.Durata ?? 30,
            comisionZi: contract.Comision_zi ?? 0.01,
            observatii: String(contract.Observatii ?? ''),
            statut: mapDatabaseStatusToDisplay(contract.Statut),
        });
        setFormErrors({});
        setModalVisible(true);
    };

    const validateForm = (): boolean => {
        const errors: FormErrors = {};

        if (!formData.clientId || !selectedClient) {
            errors.clientId = 'Please select a client first';
        }
        if (!formData.obiectAmanetat.trim()) {
            errors.obiectAmanetat = 'Please describe the pawned item';
        }
        if (formData.obiectAmanetat.length > 50) {
            errors.obiectAmanetat = 'Pawned item description cannot exceed 50 characters';
        }
        if (!formData.curs || formData.curs <= 0) {
            errors.curs = 'Exchange rate is required and must be positive';
        }
        if (!formData.durata || formData.durata <= 0) {
            errors.durata = 'Duration is required and must be positive';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleModalOk = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            // Format the data for API - backend expects specific field names
            const contractData = {
                clientId: formData.clientId,
                obiectAmanetat: formData.obiectAmanetat,
                sumainEuro: formData.sumainEuro ?? 0,
                sumainLei: formData.sumainLei ?? 0,
                curs: formData.curs ?? 5.0,
                durata: formData.durata ?? 30,
                comisionZi: formData.comisionZi ?? 0.01,
                status: mapDisplayStatusToDatabase(formData.statut),
            };

            if (editingContract) {
                try {
                    const result = await request({
                        path: `/contracts/${editingContract.Nr}`,
                        method: 'PUT',
                        params: contractData,
                    });
                    setAlertMessage({ type: 'success', text: 'Contract updated successfully' });
                    console.log('Update contract result:', result);
                    setModalVisible(false);
                    reloadContracts();
                } catch (error) {
                    setAlertMessage({ type: 'error', text: 'Failed to update contract' });
                    console.error('Update contract error:', error);
                }
            } else {
                // Create new contract
                const contractDataWithMappedStatus = {
                    ...contractData,
                    status: mapDisplayStatusToDatabase(formData.statut),
                };
                delete (contractDataWithMappedStatus as Record<string, unknown>).status;
                if (formData.statut) {
                    contractDataWithMappedStatus.status = mapDisplayStatusToDatabase(
                        formData.statut,
                    );
                }

                try {
                    const result = await request({
                        path: '/contracts',
                        method: 'POST',
                        params: contractDataWithMappedStatus,
                    });
                    console.log('Create contract result:', result);
                    setAlertMessage({ type: 'success', text: 'Contract created successfully' });
                    setModalVisible(false);
                    reloadContracts();
                } catch (error) {
                    setAlertMessage({ type: 'error', text: 'Failed to create contract' });
                    console.error('Create contract error:', error);
                }
            }
        } catch (error) {
            console.error('Form validation error:', error);
        }
    };

    const handleModalCancel = () => {
        setModalVisible(false);
        setFormData({
            clientId: 0,
            obiectAmanetat: '',
            sumainEuro: 0,
            sumainLei: 0,
            curs: 5.0,
            durata: 30,
            comisionZi: 0.01,
            observatii: '',
            statut: 'ACTIV',
        });
        setFormErrors({});
        setEditingContract(null);
    };

    const renderPawnedItems = () => {
        if (!postponeContract) {
            return <Box sx={{ color: 'text.secondary' }}>-</Box>;
        }
        if (pawnedItemsLoading) {
            return <Box sx={{ color: 'text.secondary' }}>{t('common.loading')}</Box>;
        }
        if (pawnedItemRows.length === 0) {
            return <Box sx={{ color: 'text.secondary' }}>{t('contracts.noPawnedItems')}</Box>;
        }
        return pawnedItemRows.map(row => (
            <Box key={row.key} sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                <Box sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.name}
                    {row.details && (
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                            {` (${row.details})`}
                        </Box>
                    )}
                </Box>
                <Box sx={{ flexShrink: 0, color: 'text.secondary' }}>{row.quantity}</Box>
            </Box>
        ));
    };

    const columns = [
        {
            key: 'Statut',
            title: t('contracts.status'),
            dataIndex: 'Statut',
            sortable: true,
            filterable: true,
            width: 80,
            align: 'left' as const,
            headerAlign: 'left' as const,
            render: (status: string) => <ContractStatusChip status={status} />,
        },
        {
            key: 'NrContract',
            title: 'Contract #',
            dataIndex: 'NrContract',
            sortable: true,
            filterable: true,
            width: 110,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'Nume_client',
            title: t('contracts.client'),
            dataIndex: 'Nume_client',
            sortable: true,
            filterable: true,
            width: 130,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'obiect_amanetat',
            title: t('contracts.item'),
            dataIndex: 'obiect_amanetat',
            sortable: false,
            filterable: true,
            width: 200,
            align: 'left' as const,
            headerAlign: 'left' as const,
        },
        {
            key: 'quantity',
            title: t('contracts.quantity'),
            dataIndex: 'quantity',
            sortable: false,
            filterable: false,
            width: 40,
            align: 'right' as const,
            headerAlign: 'right' as const,
        },
        ...getContractAmountColumns(t),
        {
            key: 'Curs_BNR',
            title: t('contracts.exchangeRate'),
            dataIndex: 'Curs_BNR',
            sortable: true,
            filterable: false,
            width: 80,
            align: 'right' as const,
            headerAlign: 'right' as const,
            render: (value: number | string) =>
                value ? Number.parseFloat(String(value)).toFixed(4) : '-',
        },
        {
            key: 'Comision_zi',
            title: t('contracts.dailyInterestRate'),
            dataIndex: 'Comision_zi',
            sortable: true,
            filterable: false,
            width: 80,
            align: 'right' as const,
            headerAlign: 'right' as const,
            // A special-rate contract shows its commission in bold, as Delphi bolds its row.
            render: (value: number | string, row: ContractData) => (
                <Box
                    component="span"
                    sx={{ fontWeight: row.specialRate ? 700 : undefined }}
                    title={row.specialRate ? t('contracts.specialRate.marker') : undefined}
                    data-special-rate={row.specialRate ? 'true' : undefined}
                >
                    {value ? Number.parseFloat(String(value)).toFixed(4) : '-'}
                </Box>
            ),
        },
        {
            key: 'Durata',
            title: t('contracts.durationDays'),
            dataIndex: 'Durata',
            sortable: true,
            filterable: false,
            width: 70,
            align: 'right' as const,
            headerAlign: 'right' as const,
        },
        ...getContractDateColumns(t),
        createDateColumn('extended_due_date', t('contracts.extendedDueDate')),
        createDateColumn('Data', t('contracts.contractDate')),
    ];

    /**
     * The six figures administrators see under the contracts grid. Counts are plain; the
     * money is RON at zero decimals, written the way the grid's own amount column writes it.
     * The category is the client's tier (VIP, Gold, Bonus...), stored on the client record.
     */
    /**
     * The client action buttons sit on the page canvas rather than on a card, so an outlined
     * MUI button leaves them transparent and they read as unfinished. They take the same
     * white the grids use (`.webgrid` in WebGrid.css) and the same hover the grid rows use,
     * so the column matches the tables beside it. This applies in every theme.
     */
    const clientActionButtonSx = {
        justifyContent: 'flex-start',
        textTransform: 'none',
        backgroundColor: '#fff',
        '&:hover': { backgroundColor: '#f5f5f5' },
        '&.Mui-disabled': { backgroundColor: '#fff' },
    } as const;

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
                ref={contractsPageRef}
                className={
                    clientStanding
                        ? `contracts-page contracts-page--${clientStanding}`
                        : 'contracts-page'
                }
            >
                {alertMessage && (
                    <Alert
                        severity={alertMessage.type}
                        onClose={() => setAlertMessage(null)}
                        sx={{ mb: 2 }}
                    >
                        {alertMessage.text}
                    </Alert>
                )}

                <CashBookingRefusalDialog
                    refusalKey={bookingRefusalKey}
                    onClose={() => setBookingRefusalKey(null)}
                />


                {/* Main Title */}
                <Paper
                    elevation={0}
                    sx={{
                        px: 1.5,
                        py: 0.75,
                        mb: 1.5,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        background: theme =>
                            `linear-gradient(135deg, ${alpha(
                                theme.palette.primary.main,
                                0.14,
                            )}, ${alpha(theme.palette.primary.main, 0.03)})`,
                        border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            flexShrink: 0,
                            color: 'primary.contrastText',
                            backgroundColor: 'primary.main',
                            boxShadow: theme =>
                                `0 4px 10px ${alpha(theme.palette.primary.main, 0.35)}`,
                            '& svg': { fontSize: 20 },
                        }}
                    >
                        <ContractIcon />
                    </Box>
                    <Box>
                        <Typography
                            variant="subtitle1"
                            component="h1"
                            sx={{ fontWeight: 700, lineHeight: 1.2 }}
                        >
                            {t('contracts.operatiuniContracte')}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', lineHeight: 1.2 }}
                        >
                            {t('contracts.subtitle')}
                        </Typography>
                    </Box>
                </Paper>

                {/* Top Section: Client Search Grid + Buttons + Photo */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {/* Client Search Grid */}
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: '500px',
                            position: 'relative',
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1.5,
                        }}
                    >
                        <WebGrid<ClientData>
                            data={filteredClients}
                            columns={clientColumns}
                            loading={clientsLoading}
                            pagination={true}
                            pageSize={100}
                            filterBar={true}
                            initialFilters={clientGridInitialFilters}
                            showPaginationRow={true}
                            showRecordCountRow={false}
                            exportable={false}
                            selectable={true}
                            multiSelect={false}
                            showCheckboxes={false}
                            rowIdAccessor={row => row.ClNr}
                            selectedRows={selectedClient ? [selectedClient] : []}
                            onSelectionChange={rows => handleClientSelection(rows[0] ?? null)}
                            onRowClick={client => handleClientSelection(client)}
                            onRowDoubleClick={client =>
                                navigate(
                                    `/clients?clientId=${client.ClNr}&name=${encodeURIComponent(
                                        client.Nume,
                                    )}`,
                                )
                            }
                            onFilterChange={filters => {
                                const prevFilters = prevClientFiltersRef.current;

                                // When ClNr is being typed and non-empty, clear Nume
                                if (
                                    filters.ClNr !== prevFilters.ClNr &&
                                    filters.ClNr?.trim() &&
                                    filters.Nume?.trim()
                                ) {
                                    const cleared = { ...filters, Nume: '' };
                                    prevClientFiltersRef.current = cleared;
                                    setClientGridInitialFilters(cleared);
                                }
                                // When Nume is being typed and non-empty, clear ClNr
                                else if (
                                    filters.Nume !== prevFilters.Nume &&
                                    filters.Nume?.trim() &&
                                    filters.ClNr?.trim()
                                ) {
                                    const cleared = { ...filters, ClNr: '' };
                                    prevClientFiltersRef.current = cleared;
                                    setClientGridInitialFilters(cleared);
                                } else {
                                    prevClientFiltersRef.current = filters;
                                }

                                const filtersMatchSelectedClient =
                                    selectedClient &&
                                    filters.ClNr === String(selectedClient.ClNr) &&
                                    filters.Nume === selectedClient.Nume;

                                if (
                                    selectedClient &&
                                    !filtersMatchSelectedClient &&
                                    Object.values(filters).some(filter => filter.trim() !== '')
                                ) {
                                    handleClientSelection(null);
                                }
                            }}
                            onExport={() => {}}
                            height={230}
                            emptyMessage={t('clients.noData')}
                            title=""
                        />

                        {/* Contract Number Search Input */}
                        <TextField
                            size="small"
                            placeholder={t('contracts.number')}
                            value={contractNumberSearch}
                            onChange={e => setContractNumberSearch(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    handleContractNumberSearch(contractNumberSearch);
                                }
                            }}
                            disabled={contractSearchLoading}
                            variant="outlined"
                            sx={{
                                position: 'absolute',
                                top: '33px',
                                right: '15px',
                                width: '100px',
                                zIndex: 10,
                                '& .MuiInputBase-root': {
                                    height: '26px',
                                    fontSize: '0.60rem',
                                    backgroundColor: 'white',
                                },
                            }}
                        />
                    </Box>

                    {/* Action Buttons */}
                    <Stack spacing={1} sx={{ width: '160px', flexShrink: 0 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate('/clients')}
                            sx={clientActionButtonSx}
                        >
                            {t('contracts.clientNouClienti')}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleAddContract}
                            disabled={!selectedClient || selectedClient.IsDisabled}
                            data-testid="add-contract-button"
                            sx={clientActionButtonSx}
                        >
                            {t('contracts.contractNou')}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={!selectedClient || selectedClient.IsDisabled}
                            data-testid="payment-button"
                            onClick={() => {
                                if (selectedClient) {
                                    void openBookingPage(
                                        `/contracts/payment?clientId=${selectedClient.ClNr}`,
                                    );
                                }
                            }}
                            sx={clientActionButtonSx}
                        >
                            {t('contracts.achitare')}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={selectedContracts.length === 0}
                            onClick={() => {
                                if (selectedContracts.length > 0) {
                                    setOutputModalOpen(true);
                                }
                            }}
                            sx={clientActionButtonSx}
                        >
                            {t('contracts.listeazaContract')}
                        </Button>
                        <Box sx={{ pt: '16px' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                onClick={() => setAdvancedSearchOpen(true)}
                                sx={clientActionButtonSx}
                            >
                                {t('contracts.advancedSearch.title')}
                            </Button>
                        </Box>
                    </Stack>

                    {/* Client Photo */}
                    <ClientPhoto
                        photoUrl={selectedClient ? selectedClientPhotoUrl : undefined}
                        clientName={selectedClient?.Nume}
                    />
                </Box>

                {/* Middle Section: Observatii + Name + Postpone Expiration */}
                <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                    {/* Observatii (Notes) Section */}
                    <Paper
                        sx={{
                            p: 0.75,
                            width: '300px',
                            flexShrink: 0,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            backgroundColor: '#ffffcc',
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', color: 'red', fontWeight: 'bold', mb: 0.5 }}
                        >
                            {t('contracts.observatii')}
                        </Typography>
                        <TextField
                            multiline
                            minRows={2}
                            maxRows={3}
                            fullWidth
                            size="small"
                            value={notesText}
                            onChange={e => setNotesText(e.target.value)}
                            disabled={!selectedClient || notesSaving}
                            placeholder={t('contracts.observatii')}
                            sx={{ backgroundColor: '#fff' }}
                        />
                        <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={!selectedClient || notesSaving}
                                onClick={() => saveClientNotes('')}
                            >
                                {t('contracts.sterge')}
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={!selectedClient || notesSaving}
                                onClick={() => saveClientNotes(notesText)}
                            >
                                {t('contracts.salveaza')}
                            </Button>
                        </Stack>
                    </Paper>

                    {/* Client name + Postponement card */}
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                                label={t('clients.name')}
                                value={selectedClient?.Nume ?? ''}
                                size="small"
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' },
                                }}
                                slotProps={{
                                    input: { readOnly: true },
                                    inputLabel: { shrink: true },
                                }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                data-testid="client-sheet-open"
                                disabled={!selectedClient}
                                onClick={() =>
                                    selectedClient &&
                                    navigate(`/contracts/client-sheet?clientId=${selectedClient.ClNr}`)
                                }
                                sx={{ flexShrink: 0, whiteSpace: 'nowrap', backgroundColor: '#fff' }}
                            >
                                {t('contracts.clientSheet.open')}
                            </Button>
                        </Box>

                        <Paper
                            variant="outlined"
                            data-testid="postpone-contract-panel"
                            sx={{
                                flex: 1,
                                overflow: 'hidden',
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box
                                sx={{
                                    px: 1.5,
                                    py: 0.875,
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    backgroundColor: theme =>
                                        alpha(theme.palette.primary.main, 0.05),
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    {t('contracts.amanareExpirare')}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'stretch', sm: 'center' },
                                    gap: 1.5,
                                    p: 1.5,
                                }}
                            >
                                {/* Contract selected in the contracts grid + its pawned items */}
                                <Box
                                    data-testid="postpone-contract-summary"
                                    sx={{ flex: 1, minWidth: 0 }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', fontWeight: 600, lineHeight: 1.4 }}
                                    >
                                        {t('contracts.selectedContract')}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        data-testid="postpone-contract-number"
                                        sx={{
                                            fontWeight: 700,
                                            color: postponeContract
                                                ? 'text.primary'
                                                : 'text.secondary',
                                            mb: 1,
                                        }}
                                    >
                                        {postponeContract?.NrContract ??
                                            t('contracts.selectContractForPostpone')}
                                    </Typography>
                                    <Box
                                        sx={{
                                            px: 1,
                                            py: 0.75,
                                            borderLeft: 3,
                                            borderColor: 'primary.main',
                                            backgroundColor: 'action.hover',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                display: 'block',
                                                fontWeight: 600,
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            {t('contracts.pawnedItems')}
                                        </Typography>
                                        <Box
                                            data-testid="postpone-contract-items"
                                            sx={{
                                                maxHeight: 56,
                                                overflowY: 'auto',
                                                pr: 0.5,
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            {renderPawnedItems()}
                                        </Box>
                                    </Box>
                                </Box>

                                <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{ display: { xs: 'none', sm: 'block' } }}
                                />

                                {/* Postponement date + action */}
                                <Stack
                                    data-testid="postpone-contract-action"
                                    spacing={1}
                                    sx={{
                                        width: { xs: 'auto', md: 360, lg: 420 },
                                        flexShrink: 0,
                                        p: 1,
                                        borderRadius: 1.5,
                                        backgroundColor: 'action.hover',
                                    }}
                                >
                                    <Box
                                        data-testid="postpone-contract-action-controls"
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                        <DatePicker
                                            label={t('contracts.panaLaData')}
                                            value={postponeDate}
                                            onChange={newValue => setPostponeDate(newValue)}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    fullWidth: true,
                                                },
                                            }}
                                            sx={{ flex: 1, minWidth: 0 }}
                                        />
                                        <Button
                                            variant="contained"
                                            size="small"
                                            sx={{ width: 112, flexShrink: 0 }}
                                            data-testid="postpone-save-button"
                                            disabled={postponeDisabled}
                                            onClick={handlePostponeContract}
                                        >
                                            {t('contracts.salveaza')}
                                        </Button>
                                    </Box>
                                    <Box
                                        data-testid="postpone-blocked-reason-area"
                                        sx={{
                                            minHeight: 34,
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        {postponeBlockingStatus && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                                data-testid="postpone-blocked-reason"
                                                sx={{ lineHeight: 1.3 }}
                                            >
                                                {t('contracts.postponeNotAllowedForStatus', {
                                                    status: postponeBlockingStatus,
                                                })}
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>
                </Box>

                {advancedSearchOpen && (
                    <AdvancedContractSearchDialog
                        contentElement={contractsPageRef.current?.closest('main')}
                        onClose={() => setAdvancedSearchOpen(false)}
                    />
                )}

                {/* Client File Section (Fisa Client) */}
                <Paper sx={{ p: 1, mb: 2, border: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {t('contracts.fisaClient')}
                        </Typography>
                        <Typography variant="body2">{t('contracts.categoriePropusa')}</Typography>
                        <FormControl
                            size="small"
                            sx={{ minWidth: 120 }}
                            data-testid="client-category-select"
                        >
                            <Select
                                value={selectedCategory}
                                disabled={!selectedClient || categorySaving}
                                onChange={event =>
                                    selectedClient &&
                                    setCategoryOverride({
                                        clientNr: selectedClient.ClNr,
                                        name: event.target.value,
                                    })
                                }
                            >
                                {categoryOptions.map(name => (
                                    <MenuItem key={name} value={name}>
                                        {name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="outlined"
                            size="small"
                            data-testid="client-category-activate"
                            disabled={
                                !selectedClient ||
                                categorySaving ||
                                selectedCategory === '' ||
                                categoryUnchanged
                            }
                            onClick={saveClientCategory}
                        >
                            {t('contracts.activare')}
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={!selectedClient}
                            onClick={openClientFile}
                        >
                            {t('contracts.modificare')}
                        </Button>
                        <IconButton
                            size="small"
                            onClick={() => handleClientSelection(null)}
                            title="Clear selection"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Single row client info */}
                    <ClientDetailsRow client={selectedClient} />
                </Paper>

                {/* Contracts Grid */}
                <WebGrid<ContractData>
                    data={filteredContracts}
                    columns={columns}
                    loading={contractsLoading}
                    pagination={true}
                    pageSize={20}
                    filterBar={true}
                    exportable={true}
                    selectable={true}
                    multiSelect={true}
                    rowIdAccessor={row => row.Nr}
                    selectedRows={selectedContracts}
                    onSelectionChange={rows => setSelectedContracts(rows)}
                    onRowClick={row => setSelectedContracts([row])}
                    onRowDoubleClick={handleEditContract}
                    onRowContextMenu={(event, row) => {
                        event.preventDefault();
                        setContextMenu(
                            contextMenu === null
                                ? {
                                      mouseX: event.clientX + 2,
                                      mouseY: event.clientY - 6,
                                      contract: row,
                                  }
                                : null,
                        );
                    }}
                    onExport={() => {}}
                    height={400}
                    showRecordCountRow={false}
                    getRowStyle={(row: ContractData) =>
                        row.extended_due_date && new Date(row.extended_due_date) > new Date()
                            ? { backgroundColor: '#fffacd' }
                            : undefined
                    }
                    emptyMessage={
                        selectedClient ? t('contracts.noData') : t('contracts.selectClientForData')
                    }
                    title={
                        selectedClient
                            ? `${t('contracts.title')} - ${selectedClient.Nume} (${
                                  filteredContracts.length
                              } ${t('contracts.found')})`
                            : t('contracts.title')
                    }
                />

                {/* Client summary — administrators only */}
                {isAdmin && (
                    <ClientSummary
                        summary={clientSummary}
                        category={selectedClient?.Categorie}
                        hasClient={!!selectedClient}
                    />
                )}

                <Menu
                    open={contextMenu !== null}
                    onClose={() => setContextMenu(null)}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        contextMenu !== null
                            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                            : undefined
                    }
                >
                    <MenuItem
                        onClick={() => {
                            if (contextMenu?.contract?.Nr) {
                                setSelectedHistoryContractNr(contextMenu.contract.Nr);
                            }
                            setHistoryModalOpen(true);
                            setContextMenu(null);
                        }}
                    >
                        Istoric contract
                    </MenuItem>
                    {/* Delphi enables it for an administrator on an open contract only. */}
                    <MenuItem
                        disabled={!isAdmin || !canSetSpecialRate(contextMenu?.contract?.Statut)}
                        onClick={() => {
                            const contract = contextMenu?.contract;
                            if (contract) {
                                setSpecialRateContract({
                                    nr: contract.Nr,
                                    contractNumber: contract.NrContract,
                                    commissionPerDay: contract.Comision_zi,
                                });
                            }
                            setContextMenu(null);
                        }}
                    >
                        {t('contracts.specialRate.menu')}
                    </MenuItem>
                    {contextMenu?.contract && (
                        <MenuItem
                            onClick={() => {
                                const contract = contextMenu.contract;
                                if (contract) {
                                    addLine(
                                        `Contract ${contract.NrContract} (Lei)`,
                                        contract.SumainLei || 0,
                                    );
                                    setAlertMessage({
                                        type: 'success',
                                        text: `Adăugat ${contract.SumainLei} Lei la Sumator`,
                                    });
                                }
                                setContextMenu(null);
                            }}
                        >
                            {t('sumator.addToSumator')}
                        </MenuItem>
                    )}
                </Menu>

                <SpecialRateDialog
                    contract={specialRateContract}
                    onClose={() => setSpecialRateContract(null)}
                    onSaved={commissionPerDay => {
                        setAlertMessage({
                            type: 'success',
                            text: t('contracts.specialRate.success', {
                                number: specialRateContract?.contractNumber ?? '',
                                rate: commissionPerDay,
                            }),
                        });
                        setSpecialRateContract(null);
                        reloadContracts();
                    }}
                />

                <ContractHistoryModal
                    open={historyModalOpen}
                    onClose={() => setHistoryModalOpen(false)}
                    contractNr={selectedHistoryContractNr}
                />

                <Dialog open={modalVisible} onClose={handleModalCancel} maxWidth="md" fullWidth>
                    <DialogTitle>
                        {editingContract ? t('contracts.edit') : t('contracts.add')}
                    </DialogTitle>
                    <DialogContent>
                        <Box component="form" sx={{ mt: 2 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    fullWidth
                                    label="Client"
                                    value={selectedClient?.Nume ?? 'No client selected'}
                                    disabled
                                    error={!!formErrors.clientId}
                                    helperText={
                                        formErrors.clientId ??
                                        'Select a client from the table above first'
                                    }
                                />
                                <FormControl
                                    fullWidth
                                    error={!!formErrors.statut}
                                    disabled={!!editingContract}
                                >
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={formData.statut}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                statut: e.target.value,
                                            }))
                                        }
                                        label="Status"
                                    >
                                        <MenuItem value="ACTIV">Active</MenuItem>
                                        <MenuItem value="PASIV">Passive</MenuItem>
                                        <MenuItem value="EXPIRAT">Expired</MenuItem>
                                        <MenuItem value="PRELUNGIT">Extended</MenuItem>
                                        <MenuItem value="INCHEIAT">Closed</MenuItem>
                                    </Select>
                                    {formErrors.statut && (
                                        <Typography
                                            variant="caption"
                                            color="error"
                                            sx={{ mt: 0.5 }}
                                        >
                                            {formErrors.statut}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Stack>

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                sx={{ mt: 2 }}
                            >
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label={t('contracts.objectPledged')}
                                    value={formData.obiectAmanetat}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            obiectAmanetat: e.target.value,
                                        }))
                                    }
                                    disabled={!!editingContract}
                                    error={!!formErrors.obiectAmanetat}
                                    helperText={
                                        formErrors.obiectAmanetat ??
                                        `${formData.obiectAmanetat.length}/50 characters`
                                    }
                                    slotProps={{ htmlInput: { maxLength: 50 } }}
                                />
                                {editingContract && (
                                    <TextField
                                        fullWidth
                                        label={t('contracts.quantity')}
                                        value={editingContract.quantity ?? ''}
                                        disabled
                                    />
                                )}
                            </Stack>

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                sx={{ mt: 1 }}
                            >
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('contracts.amountLei')}
                                    value={formData.sumainLei}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            sumainLei: Number(e.target.value),
                                        }))
                                    }
                                    disabled={!!editingContract}
                                    error={!!formErrors.sumainLei}
                                    helperText={formErrors.sumainLei}
                                    slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                                />
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('contracts.amountEur')}
                                    value={formData.sumainEuro}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            sumainEuro: Number(e.target.value),
                                        }))
                                    }
                                    disabled={!!editingContract}
                                    error={!!formErrors.sumainEuro}
                                    helperText={formErrors.sumainEuro}
                                    slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                                />
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('contracts.exchangeRate')}
                                    value={formData.curs}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            curs: Number(e.target.value),
                                        }))
                                    }
                                    disabled={!!editingContract}
                                    error={!!formErrors.curs}
                                    helperText={formErrors.curs}
                                    slotProps={{ htmlInput: { min: 0, step: 0.0001 } }}
                                    required
                                />
                            </Stack>

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                sx={{ mt: 1 }}
                            >
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('contracts.duration')}
                                    value={formData.durata}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            durata: Number(e.target.value),
                                        }))
                                    }
                                    error={!!formErrors.durata}
                                    helperText={formErrors.durata}
                                    slotProps={{ htmlInput: { min: 1 } }}
                                    required
                                />
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('contracts.dailyInterestRate')}
                                    value={formData.comisionZi}
                                    onChange={e =>
                                        setFormData(prev => ({
                                            ...prev,
                                            comisionZi: Number(e.target.value),
                                        }))
                                    }
                                    slotProps={{
                                        htmlInput: { min: 0, step: 0.0001 },
                                    }}
                                />
                            </Stack>

                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label={t('contracts.observations')}
                                value={formData.observatii}
                                onChange={e =>
                                    setFormData(prev => ({ ...prev, observatii: e.target.value }))
                                }
                                disabled={!!editingContract}
                                error={!!formErrors.observatii}
                                helperText={formErrors.observatii}
                                sx={{ mt: 2 }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleModalCancel}>Cancel</Button>
                        <Button onClick={handleModalOk} variant="contained">
                            {editingContract ? t('contracts.update') : t('contracts.create')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* A contract the browser would not print directly, for its viewer to print */}
                <ContractPdfPreviewDialog
                    open={blockedPrintDocument !== null}
                    contractNumber={blockedPrintDocument?.number ?? ''}
                    url={blockedPrintDocument?.url ?? null}
                    error={null}
                    printBlocked
                    onClose={closeBlockedPrintDocument}
                />

                {/* Contract Output Modal (Save PDF / Print) */}
                <ContractOutputModal
                    open={outputModalOpen}
                    onClose={() => setOutputModalOpen(false)}
                    onSelect={handleOutputSelect}
                />

                {/* Progress Modal */}
                <ProgressModal
                    open={progressModalOpen}
                    title={progressTitle}
                    progress={progressValue}
                    status={progressStatus}
                    message={progressMessage}
                    onClose={() => setProgressModalOpen(false)}
                />

                {/* Client Info Modal */}
                <Dialog
                    open={clientInfoModalOpen}
                    onClose={() => setClientInfoModalOpen(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>{t('contracts.fisaClient')}</DialogTitle>
                    <DialogContent>
                        {selectedClient && (
                            <Box
                                sx={{
                                    mt: 1,
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                    gap: 2,
                                }}
                            >
                                <TextField
                                    required
                                    label={t('clients.name')}
                                    value={clientEditData.Nume}
                                    onChange={event =>
                                        updateClientEditField('Nume', event.target.value)
                                    }
                                />
                                <TextField
                                    label={t('clients.city')}
                                    value={clientEditData.Oras}
                                    onChange={event =>
                                        updateClientEditField('Oras', event.target.value)
                                    }
                                />
                                <TextField
                                    label={t('clients.street')}
                                    value={clientEditData.Str}
                                    onChange={event =>
                                        updateClientEditField('Str', event.target.value)
                                    }
                                />
                                <Stack direction="row" spacing={2}>
                                    <TextField
                                        fullWidth
                                        label={t('clients.streetNumber')}
                                        value={clientEditData.Nr}
                                        onChange={event =>
                                            updateClientEditField('Nr', event.target.value)
                                        }
                                    />
                                    <TextField
                                        fullWidth
                                        label={t('clients.apartment')}
                                        value={clientEditData.Ap}
                                        onChange={event =>
                                            updateClientEditField('Ap', event.target.value)
                                        }
                                    />
                                </Stack>
                                <TextField
                                    label={t('clients.county')}
                                    value={clientEditData.Judet}
                                    onChange={event =>
                                        updateClientEditField('Judet', event.target.value)
                                    }
                                />
                                <TextField
                                    label={t('clients.idCardSeries')}
                                    value={clientEditData.SerieCI}
                                    onChange={event =>
                                        updateClientEditField('SerieCI', event.target.value)
                                    }
                                />
                                <TextField
                                    label={t('clients.idCardNumber')}
                                    value={clientEditData.NrCI}
                                    onChange={event =>
                                        updateClientEditField('NrCI', event.target.value)
                                    }
                                />
                                <TextField
                                    label={t('clients.idIssuedBy')}
                                    value={clientEditData.EmitentCI}
                                    onChange={event =>
                                        updateClientEditField('EmitentCI', event.target.value)
                                    }
                                />
                                <TextField
                                    label={t('clients.phone')}
                                    value={clientEditData.Telefon}
                                    onChange={event =>
                                        updateClientEditField('Telefon', event.target.value)
                                    }
                                />
                                <TextField
                                    label={t('clients.personalCode')}
                                    value={clientEditData.CNP}
                                    onChange={event =>
                                        updateClientEditField('CNP', event.target.value)
                                    }
                                />
                                <TextField
                                    label={t('clients.delegate')}
                                    value={clientEditData.Delegate}
                                    onChange={event =>
                                        updateClientEditField('Delegate', event.target.value)
                                    }
                                />
                                <TextField
                                    label={t('clients.taxCode')}
                                    value={clientEditData.TaxCode}
                                    onChange={event =>
                                        updateClientEditField('TaxCode', event.target.value)
                                    }
                                />
                                <TextField
                                    label={t('clients.registrationNumber')}
                                    value={clientEditData.RegistrationNumber}
                                    onChange={event =>
                                        updateClientEditField(
                                            'RegistrationNumber',
                                            event.target.value,
                                        )
                                    }
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={clientEditData.IsDisabled}
                                            onChange={event =>
                                                updateClientEditField(
                                                    'IsDisabled',
                                                    event.target.checked,
                                                )
                                            }
                                        />
                                    }
                                    label={t('contracts.clientDisabled')}
                                    sx={{ color: 'error.main' }}
                                />
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color="warning"
                            disabled={!clientEditData.IsDisabled || clientSaving}
                            onClick={() => void openMergeClientModal()}
                            sx={{ mr: 'auto' }}
                        >
                            {t('contracts.mergeWith')}
                        </Button>
                        <Button
                            disabled={clientSaving}
                            onClick={() => setClientInfoModalOpen(false)}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            disabled={clientSaving}
                            onClick={() => void saveClientDetails()}
                        >
                            {clientSaving ? t('common.saving') : t('contracts.saveAndClose')}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={mergeClientModalOpen}
                    onClose={() => setMergeClientModalOpen(false)}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle>{t('contracts.mergeClients')}</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Alert severity="warning">{t('contracts.mergeClientsWarning')}</Alert>
                            <TextField
                                label={t('contracts.duplicateClient')}
                                value={selectedClient?.Nume ?? ''}
                                slotProps={{ input: { readOnly: true } }}
                            />
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {t('contracts.baseClient')}
                                </Typography>
                                <Box data-testid="merge-client-candidates-grid">
                                    <WebGrid<ClientData>
                                        data={activeMergeCandidates}
                                        columns={clientColumns}
                                        loading={clientsLoading}
                                        pagination={true}
                                        pageSize={20}
                                        filterBar={true}
                                        exportable={false}
                                        selectable={true}
                                        multiSelect={false}
                                        rowIdAccessor={client => client.ClNr}
                                        selectedRows={activeMergeCandidates.filter(
                                            client => client.ClNr === mergeTargetClientId,
                                        )}
                                        onSelectionChange={rows =>
                                            setMergeTargetClientId(rows[0]?.ClNr ?? '')
                                        }
                                        onRowClick={client => setMergeTargetClientId(client.ClNr)}
                                        onExport={() => {}}
                                        height="360px"
                                        storageKey="merge-client-grid-column-widths"
                                        emptyMessage={t('clients.noData')}
                                    />
                                </Box>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            disabled={clientMerging}
                            onClick={() => setMergeClientModalOpen(false)}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            disabled={mergeTargetClientId === '' || clientMerging}
                            onClick={() => void mergeClient()}
                        >
                            {clientMerging ? t('common.processing') : t('contracts.merge')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Postponement confirmation dialog */}
                <Dialog open={postponeConfirmOpen} onClose={() => setPostponeConfirmOpen(false)}>
                    <DialogTitle>{t('contracts.postponeConfirmTitle')}</DialogTitle>
                    <DialogContent>
                        <Typography>
                            {t('contracts.postponeConfirmMessage', {
                                date: postponeDate?.format('DD.MM.YYYY') ?? '',
                            })}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPostponeConfirmOpen(false)}>
                            {t('common.no', 'No')}
                        </Button>
                        <Button variant="outlined" onClick={() => executePostpone('all')}>
                            {t('contracts.postponeAll')}
                        </Button>
                        <Button variant="contained" onClick={() => executePostpone('single')}>
                            {t('common.yes', 'Yes')}
                        </Button>
                    </DialogActions>
                </Dialog>
                <ContractObjectReturnsPanel />
            </Box>
        </LocalizationProvider>
    );
};

const Contracts = () => <ContractsInner />;

export default Contracts;
